import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchProducts, fetchCategories, fetchAreas } from '../services/api';

const Products = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All products');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    area: '',
  });

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadAreas();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await fetchProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await fetchCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadAreas = async () => {
    try {
      const data = await fetchAreas();
      setAreas(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load areas:', err);
    }
  };

  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (filters.category) {
      list = list.filter(p => p.category_id === parseInt(filters.category));
    }
    if (filters.area) {
      list = list.filter(p => p.area_id === parseInt(filters.area));
    }
    if (searchText) {
      list = list.filter(p =>
        p.name.toLowerCase().includes(searchText.toLowerCase()) ||
        p.description.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    return list;
  }, [filters, searchText, products]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    const tabMap = {
      'All products': '',
      'Electronics': 'Electronics',
      'Furniture': 'Furniture',
      'Food': 'Food',
      'Fashion': 'Fashion',
    };
    setFilters(prev => ({ ...prev, category: tabMap[tab] || '' }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    if (field === 'category') {
      const tabMap = {
        'Electronics': 'Electronics',
        'Furniture': 'Furniture',
        'Food': 'Food',
        'Fashion': 'Fashion',
      };
      setActiveTab(tabMap[value] || 'All products');
    }
  };

  const handleClearAll = () => {
    setFilters({ category: '', area: '' });
    setSearchText('');
    setActiveTab('All products');
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length;

  const renderBadge = (badge) => {
    if (!badge) return null;
    if (badge === 'verified') return <span style={styles.badgeVerified}>✓ Verified</span>;
    if (badge === 'new') return <span style={styles.badgeNew}>New</span>;
    if (badge === 'hot') return <span style={styles.badgeHot}>🔥 Hot</span>;
    return null;
  };

  return (
    <div>
      {/* PAGE HEADER */}
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Local Marketplace</h1>
        <p style={styles.pageSubtitle}>Buy products from sellers near you</p>
        <div style={styles.tabs}>
          {['All products', 'Electronics', 'Furniture', 'Food', 'Fashion'].map(tab => (
            <button
              key={tab}
              onClick={() => handleTabClick(tab)}
              style={{
                ...styles.tab,
                ...(activeTab === tab ? styles.tabActive : {}),
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* FILTER BAR */}
      <div style={styles.filterWrap}>
        <div style={styles.filterRow1}>
          <div style={styles.searchBox}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search products..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            style={{
              ...styles.filterToggleBtn,
              ...(activeFilterCount > 0 ? styles.filterToggleBtnActive : {}),
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="8" y1="12" x2="16" y2="12" />
              <line x1="11" y1="18" x2="13" y2="18" />
            </svg>
            Filters
            {activeFilterCount > 0 && <span style={styles.filterBadge}>{activeFilterCount}</span>}
          </button>
        </div>

        {/* EXPANDED FILTERS */}
        {filtersOpen && (
          <div style={styles.filterExpanded}>
            <div style={styles.filterSelects}>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                style={filters.category ? { ...styles.fselect, ...styles.fselectSelected } : styles.fselect}
              >
                <option value="">All categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <select
                value={filters.area}
                onChange={(e) => handleFilterChange('area', e.target.value)}
                style={filters.area ? { ...styles.fselect, ...styles.fselectSelected } : styles.fselect}
              >
                <option value="">All areas</option>
                {areas.map(area => (
                  <option key={area.id} value={area.id}>{area.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* LOCATION BAR */}
      <div style={styles.locationBar}>
        <div style={styles.locLeft}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          Available in nearby areas
        </div>
      </div>

      {/* PRODUCTS GRID */}
      <div style={styles.section}>
        {loading ? (
          <p style={styles.noResults}>Loading products...</p>
        ) : error ? (
          <p style={{...styles.noResults, color: '#c00'}}>{error}</p>
        ) : filteredProducts.length === 0 ? (
          <p style={styles.noResults}>No products match your filters. Try adjusting them.</p>
        ) : (
          <div style={styles.grid}>
            {filteredProducts.map(product => (
              <div key={product.id} style={styles.card}>
                <div style={styles.productImage}>
                  📦
                </div>
                <div style={styles.cardContent}>
                  <div style={styles.badgeRow}>
                    {/* {renderBadge(product.badge)} */}
                  </div>
                  <div style={styles.cardName}>{product.name}</div>
                  <div style={styles.sellerInfo}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    <span>{product.vendor?.name || 'Seller'}</span>
                  </div>
                  <div style={styles.locationInfo}>
                    📍 {product.area?.name || 'Location'}
                  </div>
                  <div style={styles.cardFooter}>
                    <div style={styles.price}>
                      ₹{product.price}
                    </div>
                    <button style={styles.contactBtn}>💬 Contact</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  pageHeader: {
    background: 'var(--surface)',
    borderBottom: '1px solid var(--border)',
    padding: '24px 20px 0',
  },
  pageTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: '28px',
    fontWeight: 700,
    color: 'var(--text-1)',
    marginBottom: '6px',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: 'var(--text-2)',
    marginBottom: '16px',
  },
  tabs: {
    display: 'flex',
    gap: 0,
    borderBottom: '1.5px solid var(--border)',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    marginBottom: 0,
  },
  tab: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-2)',
    padding: '12px 16px',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-1.5px',
    whiteSpace: 'nowrap',
    background: 'transparent',
    border: 'none',
    transition: 'all 0.2s ease',
  },
  tabActive: {
    color: 'var(--primary)',
    borderBottomColor: 'var(--primary)',
  },
  filterWrap: {
    background: 'var(--surface)',
    borderBottom: '1px solid var(--border)',
    padding: '12px 20px',
  },
  filterRow1: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'var(--surface-2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 12px',
    flex: 1,
    minWidth: 0,
    color: 'var(--text-3)',
  },
  searchInput: {
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontSize: '13px',
    color: 'var(--text-1)',
    width: '100%',
    fontFamily: 'var(--font-body)',
  },
  filterToggleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-2)',
    background: 'var(--surface-2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s',
    flexShrink: 0,
  },
  filterToggleBtnActive: {
    background: 'var(--primary-light)',
    borderColor: 'var(--primary)',
    color: 'var(--primary)',
  },
  filterBadge: {
    background: 'var(--primary)',
    color: 'white',
    fontSize: '10px',
    fontWeight: 600,
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterExpanded: {
    marginTop: '12px',
  },
  filterSelects: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '10px',
  },
  fselect: {
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    color: 'var(--text-1)',
    background: 'var(--surface-2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 30px 10px 12px',
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'11\' height=\'11\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2394A3B8\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 9px center',
    cursor: 'pointer',
    width: '100%',
    outline: 'none',
    transition: 'all 0.15s',
  },
  fselectSelected: {
    borderColor: 'var(--primary)',
    color: 'var(--primary)',
    backgroundColor: 'var(--primary-light)',
  },
  locationBar: {
    background: 'var(--accent-light)',
    borderBottom: '1px solid var(--accent)',
    padding: '12px 20px',
  },
  locLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'var(--accent)',
    fontWeight: 500,
  },
  section: {
    padding: '24px 20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '16px',
  },
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  productImage: {
    background: 'linear-gradient(135deg, var(--primary-light), #DBEAFE)',
    height: '140px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
  },
  cardContent: {
    padding: '12px',
  },
  badgeRow: {
    display: 'flex',
    gap: '6px',
    marginBottom: '8px',
    minHeight: '22px',
  },
  cardName: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text-1)',
    marginBottom: '6px',
    lineHeight: '1.4',
  },
  sellerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: 'var(--text-2)',
    marginBottom: '4px',
  },
  locationInfo: {
    fontSize: '11px',
    color: 'var(--text-2)',
    marginBottom: '10px',
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '10px',
    borderTop: '1px solid var(--border)',
    gap: '8px',
  },
  price: {
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--text-1)',
  },
  contactBtn: {
    background: 'var(--accent-light)',
    border: '1px solid var(--accent)',
    color: 'var(--accent)',
    fontSize: '12px',
    fontWeight: 600,
    padding: '6px 10px',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  },
  badgeVerified: {
    background: 'var(--accent-light)',
    color: 'var(--accent)',
    fontSize: '10px',
    fontWeight: 600,
    padding: '3px 8px',
    borderRadius: '14px',
    border: '0.5px solid var(--accent)',
    display: 'inline-block',
  },
  badgeNew: {
    background: '#F3E8FF',
    color: '#7C3AED',
    fontSize: '10px',
    fontWeight: 600,
    padding: '3px 8px',
    borderRadius: '14px',
    border: '0.5px solid #E9D5FF',
    display: 'inline-block',
  },
  badgeHot: {
    background: '#FEF3C7',
    color: 'var(--warning)',
    fontSize: '10px',
    fontWeight: 600,
    padding: '3px 8px',
    borderRadius: '14px',
    border: '0.5px solid #FCD34D',
    display: 'inline-block',
  },
  noResults: {
    color: 'var(--text-2)',
    fontSize: '14px',
    padding: '40px 20px',
    textAlign: 'center',
  },
};

export default Products;
